import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { CollapsibleGroup, GroupFrame } from '@/components/CollapsibleGroup';
import { MatchPreviewCard } from '@/components/MatchPreviewCard';
import { isCompleted, isLive } from '@/constants/matchStatus';
import type { LeagueMatch, MatchesByDateLeagueGroup } from '@/types/league';
import styles from './styles.module.scss';

/**
 * Single source of the league-match → flat `MatchPreviewCard` row mapping.
 * Rows link to the standalone match page; unresolved (both-placeholder)
 * fixtures stay non-interactive inside `MatchPreviewCard`. Memoised: React
 * Query's structural sharing keeps unchanged match objects referentially
 * stable across polls, so only rows whose match changed re-render.
 */
export const MatchRow = React.memo<{ match: LeagueMatch }>(({ match }) => {
  // Scores are only meaningful once the match has kicked off.
  const showScore = isCompleted(match.status) || isLive(match.status);
  return (
    <MatchPreviewCard
      variant="row"
      matchId={match.id}
      to={`/match/${match.id}`}
      team1Name={match.team1}
      team2Name={match.team2}
      team1Placeholder={match.team1_placeholder}
      team2Placeholder={match.team2_placeholder}
      team1LogoUrl={match.team1_logo_url}
      team2LogoUrl={match.team2_logo_url}
      score1={showScore ? match.score_team1 : null}
      score2={showScore ? match.score_team2 : null}
      status={match.status}
      time={format(new Date(match.match_date), 'HH:mm')}
      venue={match.venue}
      elapsedMinutes={match.elapsed_minutes}
      elapsedExtraMinutes={match.elapsed_extra_minutes}
      elapsedUpdatedAt={match.elapsed_updated_at}
      testId={`match-row-${match.id}`}
    />
  );
});
MatchRow.displayName = 'MatchRow';

export interface MatchRowsCardProps {
  matches: readonly LeagueMatch[];
  testId?: string;
}

/** Header-less framed list of match rows (e.g. the "By time" mode). */
export const MatchRowsCard: React.FC<MatchRowsCardProps> = ({ matches, testId }) => (
  <GroupFrame testId={testId}>
    {matches.map(match => <MatchRow key={match.id} match={match} />)}
  </GroupFrame>
);

export interface LeagueMatchesGroupProps {
  league: MatchesByDateLeagueGroup['league'];
  matches: readonly LeagueMatch[];
  expanded: boolean;
  onToggle: () => void;
}

/**
 * One league bucket of the `/matches` list: a collapsible card whose title
 * links to the league page and whose body lists the matches as flat rows.
 */
export const LeagueMatchesGroup: React.FC<LeagueMatchesGroupProps> = ({
  league,
  matches,
  expanded,
  onToggle,
}) => {
  const { t } = useTranslation('league');
  const fullName = league.country ? `${league.country} · ${league.name}` : league.name;

  const toggleLabel = expanded
    ? t('league:collapseGroup', { name: fullName })
    : t('league:expandGroup', { name: fullName, count: matches.length });

  const title = (
    <Link
      to={`/matches/${league.id}`}
      className={styles.link}
      title={fullName}
      data-testid={`matches-by-date-league-${league.id}`}
    >
      {league.logoUrl && (
        <img
          className={styles.logo}
          src={league.logoUrl}
          alt=""
          width={20}
          height={20}
          decoding="async"
        />
      )}
      <span className={styles.text}>
        {league.country && <span className={styles.country}>{league.country} · </span>}
        <span className={styles.name}>{league.name}</span>
      </span>
    </Link>
  );

  return (
    <CollapsibleGroup
      id={`matches-group-${league.id}`}
      title={title}
      count={matches.length}
      expanded={expanded}
      onToggle={onToggle}
      toggleLabel={toggleLabel}
      toggleTestId={`matches-group-toggle-${league.id}`}
    >
      {matches.map(match => <MatchRow key={match.id} match={match} />)}
    </CollapsibleGroup>
  );
};

export default LeagueMatchesGroup;
